import { Injectable } from '@angular/core';
import { CONSTANTS } from '../../Constants/Constants';
import { SellPriceCalculationResult, SellPriceConfig } from '../../models/sol-estimate-table/SellPriceConfig';
import { EscalationData as EscalationDataDTO, EstimateTab, EstimateGrossMargin, EstimateSellPrice, Escalations, SQWebProject } from '../../models/dtos';
import { EstimateCommonFunctionsService } from './estimate-common-functions.service';
import { CommonDataService } from '../common-data.service';
import {
  EstimateUiModel,
  EstimateLaborCostsModel,
  EstimateRowModel,
} from '../../models/sol-estimate-table/ui-models/index';
import { EstimateLastModifiedSellPriceService, LaborCategoryKey } from './estimate-last-modified-sell-price.service';

/** Maps each labor category to its stored sell-price and GM% fields. */
const LABOR_ROW_FIELDS: Record<LaborCategoryKey, { sellPrice: keyof EstimateSellPrice; gmPercent: keyof EstimateGrossMargin }> = {
  projectManagement: { sellPrice: 'pmSellPrice', gmPercent: 'projectManagementGMPercent' },
  engineering: { sellPrice: 'engSellPrice', gmPercent: 'engineeringGMPercent' },
  networkEngineering: { sellPrice: 'nEngineeringSellPrice', gmPercent: 'nEngineeringGMPercent' },
  qualificationEngineering: { sellPrice: 'qEngineeringSellPrice', gmPercent: 'qEngineeringGMPercent' },
  cyberSecurityEngineering: { sellPrice: 'cEngineeringSellPrice', gmPercent: 'cEngineeringGMPercent' },
  energyEngineering: { sellPrice: 'eEngineeringSellPrice', gmPercent: 'eEngineeringGMPercent' },
  electricalEngineering: { sellPrice: 'lEngineeringSellPrice', gmPercent: 'lEngineeringGMPercent' },
  graphics: { sellPrice: 'grapSellPrice', gmPercent: 'graphicsGMPercent' },
  programming: { sellPrice: 'progSellPrice', gmPercent: 'programmingGMPercent' },
  specialist: { sellPrice: 'specSellPrice', gmPercent: 'specialistGMPercent' },
  digitalDeploymentSpecialist: { sellPrice: 'ddepSellPrice', gmPercent: 'digitalDeploymentGMPercent' },
  mechanical: { sellPrice: 'mechSellPrice', gmPercent: 'mechanicalLaborGMPercent' },
  electrical: { sellPrice: 'elecSellPrice', gmPercent: 'electricianGMPercent' }
};

/**
 * Handles all labor-costs-related logic for the estimate tab.
 * Works exclusively with EstimateUiModel — no FormGroup references.
 */
@Injectable({
  providedIn: 'root'
})
export class SolEstimateLaborService {

  private static readonly LABOR_TYPES: readonly LaborCategoryKey[] = [
    'projectManagement', 'engineering', 'networkEngineering', 'qualificationEngineering',
    'cyberSecurityEngineering', 'energyEngineering', 'electricalEngineering',
    'graphics', 'programming', 'specialist', 'digitalDeploymentSpecialist',
    'mechanical', 'electrical'
  ] as const;

  constructor(
    private readonly estimateCommonFunctions: EstimateCommonFunctionsService,
    private readonly commonDataService: CommonDataService,
    private readonly lastModifiedSellPriceService: EstimateLastModifiedSellPriceService
  ) { }

  // ── Patching (initial load) ────────────────────────────────────

  patchLaborCosts(
    model: EstimateUiModel,
    config: SellPriceConfig,
    subTotalsCost: number,
    sqWebProject?: SQWebProject
  ): void {
    const gm: EstimateGrossMargin | undefined = this.commonDataService.getOtherMetaData()?.grossMargin;

    if (gm === null || gm === undefined) {
      return;
    }

    // Get escalation rates for escalation enabled status
    const escalationRates: Escalations | undefined = this.commonDataService.getEstimateMetaData()?.escalationRates;

    // Use SQWebProject labor category sumUpCost
    this.patchLaborRow(model.laborCosts.projectManagement, sqWebProject.projectManagerCategory.sumUpCost, gm.projectManagementGMPercent, escalationRates?.pmLaborEscalation);
    this.patchLaborRow(model.laborCosts.engineering, sqWebProject.engineerCategory.sumUpCost, gm.engineeringGMPercent, escalationRates?.engLaborEscalation);
    this.patchLaborRow(model.laborCosts.networkEngineering, sqWebProject.networkEngineerCategory.sumUpCost, gm.nEngineeringGMPercent, escalationRates?.nengLaborEscalation);
    this.patchLaborRow(model.laborCosts.qualificationEngineering, sqWebProject.qualificationEngineerCategory.sumUpCost, gm.qEngineeringGMPercent, escalationRates?.qengLaborEscalation);
    this.patchLaborRow(model.laborCosts.cyberSecurityEngineering, sqWebProject.cyberSecEngineerCategory.sumUpCost, gm.cEngineeringGMPercent, escalationRates?.cengLaborEscalation);
    this.patchLaborRow(model.laborCosts.energyEngineering, sqWebProject.energyEngineerCategory.sumUpCost, gm.eEngineeringGMPercent, escalationRates?.eengLaborEscalation);
    this.patchLaborRow(model.laborCosts.electricalEngineering, sqWebProject.lengEngineerCategory.sumUpCost, gm.lEngineeringGMPercent, escalationRates?.lengLaborEscalation);
    this.patchLaborRow(model.laborCosts.graphics, sqWebProject.graphicsCategory.sumUpCost, gm.graphicsGMPercent, escalationRates?.grapLaborEscalation);
    this.patchLaborRow(model.laborCosts.programming, sqWebProject.programmingCategory.sumUpCost, gm.programmingGMPercent, escalationRates?.progLaborEscalation);
    this.patchLaborRow(model.laborCosts.specialist, sqWebProject.specialistCategory.sumUpCost, gm.specialistGMPercent, escalationRates?.specLaborEscalation);
    this.patchLaborRow(model.laborCosts.digitalDeploymentSpecialist, sqWebProject.ddsCategory.sumUpCost, gm.digitalDeploymentGMPercent, escalationRates?.ddepLaborEscalation);
    this.patchLaborRow(model.laborCosts.mechanical, sqWebProject.mechanicalCategory.sumUpCost, gm.mechanicalLaborGMPercent, escalationRates?.mechLaborEscalation);
    this.patchLaborRow(model.laborCosts.electrical, sqWebProject.electricalCategory.sumUpCost, gm.electricianGMPercent, escalationRates?.elecLaborEscalation);

    this.recalculateLaborSellPrices(model, config, subTotalsCost);
  }

  private patchLaborRow(row: EstimateRowModel, cost: number, gmPercent: number, escalationData?: EscalationDataDTO): void {
    row.cost = cost;
    row.grossMarginPercentage = gmPercent;
    row.escalate = escalationData?.escalationEnabled ? CONSTANTS.Y : CONSTANTS.N;
  }

  // ── Recalculation ──────────────────────────────────────────────

  recalculateLaborSellPrices(model: EstimateUiModel, config: SellPriceConfig, subTotalsCost?: number): void {
    const stc = subTotalsCost ?? model.subTotals.cost;

    let totalCost = CONSTANTS.ZERO;
    let totalSellPrice = CONSTANTS.ZERO;

    for (const key of SolEstimateLaborService.LABOR_TYPES) {
      const result = this.calculateAndUpdateLaborRow(model.laborCosts[key], config, stc, key);
      totalCost += result.cost;
      totalSellPrice += result.sellPrice;
    }

    this.estimateCommonFunctions.updateSectionTotalsHeader(
      model.laborCosts.laborCostsHeader,
      totalCost, totalSellPrice, stc, config
    );
  }

  private calculateAndUpdateLaborRow(
    row: EstimateRowModel,
    config: SellPriceConfig,
    stc: number,
    laborKey: LaborCategoryKey
  ): { cost: number; sellPrice: number } {
    const result = config.isFixedPriceSelected !== null
      ? this.computeFixedPriceResult(row, config, laborKey)
      : this.computeLineItemResult(row, config, laborKey);
    this.estimateCommonFunctions.updateRowResult(row, result, stc);
    return { cost: row.cost, sellPrice: result.sellPrice };
  }

  private computeFixedPriceResult(
    row: EstimateRowModel,
    config: SellPriceConfig,
    laborKey: LaborCategoryKey
  ): SellPriceCalculationResult {
    const presentPercent = row.grossMarginPercentage;
    const gmPercent = this.estimateCommonFunctions.resolveRowGMPercent(
      config, Number.isNaN(presentPercent) ? undefined : presentPercent
    );
    const preservedSellPrice = this.estimateCommonFunctions.resolvePreservedSellPrice(
      row, config, this.lastModifiedSellPriceService.isLaborSellPriceLastModified(laborKey)
    );
    return this.estimateCommonFunctions.calculateRowSellPrice(
      row.cost, gmPercent, config.isMarkUpSelected, preservedSellPrice
    );
  }

  private computeLineItemResult(
    row: EstimateRowModel,
    config: SellPriceConfig,
    laborKey: LaborCategoryKey
  ): SellPriceCalculationResult {
    if (this.lastModifiedSellPriceService.isLaborSellPriceLastModified(laborKey)) {
      const storedSellPrice = this.getLaborSellPrice(laborKey);
      return this.estimateCommonFunctions.recomputePercentsFromSellPrice(row.cost, storedSellPrice);
    }
    const gmPercent = this.getLaborGMPercent(laborKey);
    return this.estimateCommonFunctions.calculateRowSellPrice(row.cost, gmPercent, config.isMarkUpSelected);
  }

  /** Returns the stored sell price for a given labor category from OtherMetaData.sellPrice. */
  getLaborSellPrice(laborKey: LaborCategoryKey): number {
    const sellPrices = this.commonDataService.getOtherMetaData()?.sellPrice;
    if (!sellPrices) { return CONSTANTS.ZERO; }
    return (sellPrices[LABOR_ROW_FIELDS[laborKey].sellPrice] as number) ?? CONSTANTS.ZERO;
  }

  /** Returns the stored GM% for a given labor category from OtherMetaData.grossMargin. */
  getLaborGMPercent(laborKey: LaborCategoryKey): number {
    const grossMargin = this.commonDataService.getOtherMetaData()?.grossMargin;
    if (!grossMargin) { return CONSTANTS.ZERO; }
    return (grossMargin[LABOR_ROW_FIELDS[laborKey].gmPercent] as number) ?? CONSTANTS.ZERO;
  }

  // ── Totals Recalculation ───────────────────────────────────────

  recalculateLaborTotals(model: EstimateUiModel, config: SellPriceConfig): void {
    const keys = SolEstimateLaborService.LABOR_TYPES;

    let totalCost = CONSTANTS.ZERO;
    let totalSellPrice = CONSTANTS.ZERO;

    for (const key of keys) {
      const row = model.laborCosts[key];
      totalCost += row.cost;
      totalSellPrice += row.sellPrice;
    }

    this.estimateCommonFunctions.updateSectionTotalsHeader(
      model.laborCosts.laborCostsHeader,
      totalCost, totalSellPrice, model.subTotals.cost, config
    );
  }

  // ── Reactive Handler ───────────────────────────────────────────

  onLaborRowPercentChanged(
    model: EstimateUiModel,
    config: SellPriceConfig,
    laborKey: Exclude<keyof EstimateLaborCostsModel, 'laborCostsHeader'>,
    newPercent: number,
    isGMPercent: boolean
  ): void {
    const row = model.laborCosts[laborKey];
    const cost = row.cost;

    const result = this.estimateCommonFunctions.calculateRowSellPrice(cost, newPercent, config.isMarkUpSelected);

    this.estimateCommonFunctions.updateRowResult(row, result, model.subTotals.cost);
    this.recalculateLaborTotals(model, config);
  }

  /**
   * Called when user edits a labor row's sell price directly (Line Item mode only).
   * Derives GM%, MU%, and TDC% from the new sell price and row cost, then
   * recalculates the labor section totals header.
   */
  onLaborRowSellPriceChanged(
    model: EstimateUiModel,
    laborKey: Exclude<keyof EstimateLaborCostsModel, 'laborCostsHeader'>,
    newSellPrice: number,
    config: SellPriceConfig
  ): void {
    const row = model.laborCosts[laborKey];
    const cost = row.cost;

    const result = this.estimateCommonFunctions.recomputePercentsFromSellPrice(cost, newSellPrice);
    this.estimateCommonFunctions.updateRowResult(row, result, model.subTotals.cost);
    this.recalculateLaborTotals(model, config);
  }

  // ── Refresh Displayed Percentages (GM↔MU toggle) ──────────────

  refreshLaborPercentages(model: EstimateUiModel): void {
    const laborKeys = SolEstimateLaborService.LABOR_TYPES;
    for (const key of laborKeys) {
      this.estimateCommonFunctions.refreshRowPercentsFromSellPrice(model.laborCosts[key]);
    }
    this.estimateCommonFunctions.refreshRowPercentsFromSellPrice(model.laborCosts.laborCostsHeader);
  }


}
